import { brDate, dayKey, daysSince, hourMinute, instantOf } from './datetime';
import { pageRange } from './pagination';

/** Cabeçalho do calendário, começando no domingo como o `getDay()` do JS. */
export const WEEK_DAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

/**
 * O período escolhido no filtro.
 *
 * Dia e hora ficam separados porque é assim que se escolhe: o calendário marca
 * os dias, os dois campos de hora recortam dentro deles. `end` vazio com
 * `start` preenchido é o dia único ainda em seleção — o caso mais comum, "o que
 * aconteceu hoje entre tal e tal hora".
 */
export interface AuditRange {
  /** `YYYY-MM-DD`; vazio = sem corte. */
  start: string;
  end: string;
  /** `HH:MM`. */
  fromTime: string;
  toTime: string;
}

export const EMPTY_RANGE: AuditRange = { start: '', end: '', fromTime: '00:00', toTime: '23:59' };

/** Os atalhos de período, como no design. */
export const RANGE_PRESETS = [
  { id: 'hoje', label: 'Hoje', days: 0 },
  { id: '7d', label: 'Últimos 7 dias', days: 6 },
  { id: '30d', label: 'Últimos 30 dias', days: 29 },
] as const;

export type RangePresetId = (typeof RANGE_PRESETS)[number]['id'];

/** O período que um atalho representa: dos N dias atrás até hoje, dia inteiro. */
export function presetRange(days: number): AuditRange {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);

  return { start: dayKey(from), end: dayKey(to), fromTime: '00:00', toTime: '23:59' };
}

/**
 * As bordas do período em ISO, que é o que o servidor recebe.
 *
 * Sem `start` não há corte nenhum. Com `start` e sem `end`, o fim é o próprio
 * dia inicial: enquanto a segunda data não foi escolhida, o filtro vale para
 * aquele dia, e não para tudo dali em diante.
 */
export function rangeBounds(range: AuditRange): { from: string | null; to: string | null } {
  if (!range.start) return { from: null, to: null };

  return {
    from: instantOf(range.start, range.fromTime).toISOString(),
    to: instantOf(range.end || range.start, range.toTime).toISOString(),
  };
}

/** O texto do botão do período. */
export function rangeLabel(range: AuditRange): string {
  if (!range.start) return 'Qualquer período';

  if (range.end && range.end !== range.start) {
    return `${brDate(range.start)} ${range.fromTime} → ${brDate(range.end)} ${range.toTime}`;
  }

  return `${brDate(range.start)} · ${range.fromTime}–${range.toTime}`;
}

/**
 * Um clique no calendário.
 *
 * Sem início, com o intervalo já fechado, ou antes do início: recomeça a
 * seleção. Só o clique depois de um início solto fecha o intervalo — assim
 * escolher a mesma data duas vezes nunca deixa o filtro invertido.
 */
export function pickDay(range: AuditRange, key: string): AuditRange {
  if (!range.start || range.end || key < range.start) {
    return { ...range, start: key, end: '' };
  }

  return { ...range, end: key };
}

/** Um quadradinho do calendário. `blank` preenche o começo do mês. */
export interface CalendarDay {
  key: string;
  label: string;
  blank: boolean;
  /** Dentro do intervalo, mas nem início nem fim: recebe o fundo contínuo. */
  inRange: boolean;
  isEdge: boolean;
  isStart: boolean;
  isEnd: boolean;
  isToday: boolean;
}

/**
 * A grade do mês. O mês começa em um dia qualquer da semana, e as células vazias
 * antes do dia 1 existem para que cada coluna continue sendo o mesmo dia da
 * semana — sem elas o calendário fica ilegível.
 */
export function monthDays(month: Date, range: AuditRange): CalendarDay[] {
  const year = month.getFullYear();
  const index = month.getMonth();
  const firstWeekDay = new Date(year, index, 1).getDay();
  const total = new Date(year, index + 1, 0).getDate();
  const today = dayKey(new Date());

  const blanks: CalendarDay[] = Array.from({ length: firstWeekDay }, (_, position) => ({
    key: `blank-${position}`,
    label: '',
    blank: true,
    inRange: false,
    isEdge: false,
    isStart: false,
    isEnd: false,
    isToday: false,
  }));

  const days: CalendarDay[] = Array.from({ length: total }, (_, position) => {
    const key = dayKey(new Date(year, index, position + 1));
    const isStart = key === range.start;
    const isEnd = key === range.end;

    return {
      key,
      label: String(position + 1),
      blank: false,
      inRange: Boolean(range.start && range.end && key > range.start && key < range.end),
      isEdge: isStart || isEnd,
      isStart,
      isEnd,
      isToday: key === today,
    };
  });

  return [...blanks, ...days];
}

/** `agosto de 2026` — o título do calendário. */
export function monthTitle(month: Date): string {
  return month.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

export function shiftMonth(month: Date, by: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + by, 1);
}

/**
 * "Hoje 16:38", "Ontem 14:02", "Qua 09:15", "12/08 08:00".
 *
 * A auditoria sempre mostra o dia, mesmo hoje — diferente da central de
 * notificações, que pode omiti-lo. Aqui a lista é o registro do que se fez, e
 * uma hora solta ao lado de outra de três dias atrás confunde as duas.
 */
export function auditWhen(createdAt: string): string {
  const date = new Date(createdAt);
  const time = hourMinute(date);
  const days = daysSince(createdAt);

  if (days <= 0) return `Hoje ${time}`;
  if (days === 1) return `Ontem ${time}`;
  if (days < 7) {
    return `${['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][date.getDay()]} ${time}`;
  }

  return `${date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${time}`;
}

/**
 * O rodapé da lista. Vazio não é o mesmo que vazio: sem filtro significa que
 * ninguém fez nada ainda; com filtro, que o recorte não pegou nada — e só o
 * segundo sugere mexer nos campos.
 */
export function auditPageLabel(
  page: number,
  pageSize: number,
  total: number,
  filtered: boolean,
): string {
  if (!total) {
    return filtered
      ? 'Nenhum registro no período selecionado'
      : 'Nenhuma ação registrada ainda';
  }

  return `${pageRange(page, pageSize, total)} registros${filtered ? ' (filtrado)' : ''}`;
}

/** `AB` — as iniciais do círculo, como no design. */
export function actorInitials(actor: string): string {
  const letters = actor
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('');

  return letters.toUpperCase() || '?';
}
